import {
  MAX_INSTALLMENTS,
  type MachineRuleInput,
  PAYMENT_METHOD_LABELS,
  type PaymentMethodValue,
} from '@crm/shared';
import { Button, Input, Label, Modal } from '@crm/ui';
import { useEffect, useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { type PaymentMachine, useReplaceMachineRules } from '../api';

/** Uma linha da tabela: pix, debito e cada parcelamento do credito. */
interface RuleRow extends MachineRuleInput {
  key: string;
  label: string;
}

function toRows(machine: PaymentMachine): RuleRow[] {
  const find = (method: PaymentMethodValue, installments: number) =>
    machine.rules.find((rule) => rule.method === method && rule.installments === installments);

  const rows: RuleRow[] = [
    {
      key: 'PIX',
      label: 'Pix',
      method: 'PIX',
      installments: 1,
      feePercent: find('PIX', 1)?.feePercent ?? 0,
      daysToReceive: find('PIX', 1)?.daysToReceive ?? 0,
    },
    {
      key: 'DEBIT',
      label: 'Debito',
      method: 'DEBIT',
      installments: 1,
      feePercent: find('DEBIT', 1)?.feePercent ?? 0,
      daysToReceive: find('DEBIT', 1)?.daysToReceive ?? 1,
    },
  ];

  for (let installments = 1; installments <= MAX_INSTALLMENTS; installments += 1) {
    const rule = find('CREDIT', installments);

    rows.push({
      key: `CREDIT-${installments}`,
      label: `Credito ${installments}x`,
      method: 'CREDIT',
      installments,
      feePercent: rule?.feePercent ?? 0,
      daysToReceive: rule?.daysToReceive ?? 30,
    });
  }

  return rows;
}

interface MachineRulesDialogProps {
  machine: PaymentMachine | null;
  onClose: () => void;
}

/**
 * Taxas e prazos da maquininha. A edicao em lote existe porque quase todo
 * salao usa a mesma taxa em todas as parcelas: digita uma vez e aplica.
 */
export function MachineRulesDialog({ machine, onClose }: MachineRulesDialogProps) {
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [bulkFee, setBulkFee] = useState('');
  const replaceRules = useReplaceMachineRules();

  useEffect(() => {
    if (machine) setRows(toRows(machine));
  }, [machine]);

  function setRow(key: string, patch: Partial<RuleRow>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  /** Aplica a taxa digitada em todas as parcelas do credito. */
  function applyToCredit() {
    const fee = Number(bulkFee.replace(',', '.'));
    if (!Number.isFinite(fee)) return;

    setRows((current) =>
      current.map((row) => (row.method === 'CREDIT' ? { ...row, feePercent: fee } : row)),
    );
  }

  /** Copia o prazo da primeira parcela do credito para as demais. */
  function copyFirstCreditTerm() {
    const first = rows.find((row) => row.method === 'CREDIT' && row.installments === 1);
    if (!first) return;

    setRows((current) =>
      current.map((row) =>
        row.method === 'CREDIT' ? { ...row, daysToReceive: first.daysToReceive } : row,
      ),
    );
  }

  function save() {
    if (!machine) return;

    replaceRules.mutate(
      {
        id: machine.id,
        rules: rows.map((row) => ({
          method: row.method,
          installments: row.installments,
          feePercent: row.feePercent,
          daysToReceive: row.daysToReceive,
        })),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      open={machine !== null}
      onClose={onClose}
      title={machine ? `Taxas da ${machine.label}` : 'Taxas'}
      subtitle="Quanto a maquininha cobra e em quantos dias o dinheiro cai."
      size="large"
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        { label: 'Salvar', onClick: save, loading: replaceRules.isPending },
      ]}
    >
      <div className="space-y-4">
        {replaceRules.error ? (
          <p className="text-sm text-destructive">{toErrorMessage(replaceRules.error)}</p>
        ) : null}

        {/* Edicao em lote: o caminho curto para quem tem taxa unica. */}
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/40 p-3">
          <div className="space-y-2">
            <Label htmlFor="bulk-fee">Taxa para todas as parcelas</Label>
            <Input
              id="bulk-fee"
              inputMode="decimal"
              placeholder="3,19"
              className="w-32"
              value={bulkFee}
              onChange={(event) => setBulkFee(event.target.value)}
            />
          </div>
          <Button type="button" variant="outline" onClick={applyToCredit}>
            Aplicar no credito
          </Button>
          <Button type="button" variant="ghost" onClick={copyFirstCreditTerm}>
            Copiar prazo da 1x
          </Button>
        </div>

        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {row.method === 'CREDIT' ? row.label : PAYMENT_METHOD_LABELS[row.method]}
              </span>

              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  aria-label={`Taxa de ${row.label}`}
                  className="w-24 text-right"
                  value={row.feePercent}
                  onChange={(event) =>
                    setRow(row.key, { feePercent: Number(event.target.value || 0) })
                  }
                />
                %
              </span>

              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                D+
                <Input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  aria-label={`Prazo de ${row.label}`}
                  className="w-20 text-right"
                  value={row.daysToReceive}
                  onChange={(event) =>
                    setRow(row.key, { daysToReceive: Number(event.target.value || 0) })
                  }
                />
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
